// All scoring sub-components for the Betting flow.
// Extracted from Betting.js to keep that file under ~700 lines.
//
// Includes:
//  • Shared building blocks: ScoringHeader, HolePills, Settlement, LiveScorecard, ScoreRow, BottomSheet, SideBetSheet
//  • Per-game scoring views: Generic / Nassau / Skins / Wolf / BBB / MatchPlay / Stableford / Hammer / Dots / Trash / BestBall / Scramble
//  • LiveScoring router that picks the right view based on game type
//  • GameRulesModal (used by ScoringHeader and shared between views)

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ArrowLeft, Check, X, Handshake, Target, Crosshair, HelpCircle, Edit3, Hammer } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

import { API, GAME_TYPES, SIDE_BET_TYPES, GAME_RULES } from './constants';

// Shared class-name helpers (keep JSX free of nested ternaries)
const signColor = (n) => {
  if (n > 0) return 'text-green-600';
  if (n < 0) return 'text-red-500';
  return 'text-muted-foreground';
};
const scoreColor = (score, par) => {
  if (score == null) return 'text-muted-foreground/30';
  if (score < par) return 'text-green-600';
  if (score > par) return 'text-red-500';
  return 'text-foreground';
};
const holeBtnClass = (isActive, isDone) => {
  if (isActive) return 'bg-primary text-primary-foreground';
  return isDone ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground';
};
const matchStatusText = (diff, p1, p2) => {
  if (diff === 0) return 'All Square';
  return diff > 0 ? `${p1} ${diff} UP` : `${p2} ${Math.abs(diff)} UP`;
};
const relToPar = (score, par) => {
  const diff = score - par;
  if (diff === 0) return 'E';
  return diff < 0 ? diff : `+${diff}`;
};
const holeChipClass = (winnerIdx, hasScores) => {
  if (winnerIdx === 0) return 'bg-primary text-primary-foreground';
  if (winnerIdx === 1) return 'bg-secondary text-secondary-foreground';
  return hasScores ? 'bg-muted text-muted-foreground' : 'bg-muted/30 text-muted-foreground/30';
};
const PART_TO_SEG = { front: 'front_9', back: 'back_9' };
const pairNetLabel = (pw) => {
  if (pw.net === 0) return 'Even';
  const leader = pw.net > 0 ? pw.p1 : pw.p2;
  return `${leader.split(' ')[0]} +$${Math.abs(pw.net).toFixed(0)}`;
};
const lowerOf = (scores, p1, p2) => {
  if (scores[p1] < scores[p2]) return p1;
  if (scores[p2] < scores[p1]) return p2;
  return null;
};
const wolfSaveLabel = (saving, wolfData, hole) => {
  if (saving) return 'Saving...';
  return wolfData ? `Save Hole ${hole}` : 'Pick partner first';
};

// =================== GAME RULES MODAL ===================
export const GameRulesModal = ({ gameType, open, onClose }) => {
  const rules = GAME_RULES[gameType];
  if (!rules || !open) return null;
  const gameInfo = GAME_TYPES.find(g => g.value === gameType);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card rounded-2xl border border-border shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-testid="game-rules-modal">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-black bg-primary/10 text-primary">{gameInfo?.icon}</div>
            <div>
              <h2 className="font-serif text-lg font-bold text-foreground">{rules.title}</h2>
              <p className="text-xs text-muted-foreground">{rules.summary}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-muted transition-colors" data-testid="close-rules-btn">
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-4 space-y-4" style={{ maxHeight: 'calc(80vh - 80px)' }}>
          {rules.sections.map((s) => (
            <div key={s.heading}>
              <h3 className="text-sm font-bold text-foreground mb-1">{s.heading}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// =================== PAR EDITOR ===================
// Tappable par badge used in every scoring view. Lets users correct the par
// on the fly when course data was missing (e.g., a Google Places course).
export const ParEditor = ({ roundId, holeNumber, par, onUpdated }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async (newPar) => {
    setSaving(true);
    try {
      await axios.put(`${API}/betting/rounds/${roundId}/par`, {
        hole_number: holeNumber,
        par: newPar,
      });
      toast.success(`Hole ${holeNumber} set to par ${newPar}`);
      setEditing(false);
      onUpdated && onUpdated();
    } catch (e) {
      toast.error('Failed to update par');
    } finally { setSaving(false); }
  };

  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary hover:bg-primary/20 active:scale-95 transition-all"
        data-testid={`par-edit-trigger-${holeNumber}`}
        title="Tap to edit this hole's par"
      >
        Par {par || 4}
        <Edit3 className="w-3 h-3 opacity-70" />
      </button>
    );
  }

  return (
    <span className="inline-flex items-center gap-1" data-testid={`par-edit-${holeNumber}`}>
      <span className="text-[10px] text-muted-foreground uppercase font-bold">Par</span>
      {[3, 4, 5, 6].map(p => (
        <button
          key={p}
          onClick={() => save(p)}
          disabled={saving}
          className={`w-7 h-7 rounded-md text-xs font-black transition-all ${par === p ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground hover:bg-primary/20'}`}
          data-testid={`par-set-${holeNumber}-${p}`}
        >
          {p}
        </button>
      ))}
      <button
        onClick={() => setEditing(false)}
        className="w-7 h-7 rounded-md text-xs text-muted-foreground hover:bg-muted"
        data-testid={`par-edit-cancel-${holeNumber}`}
      >
        <X className="w-3 h-3 inline" />
      </button>
    </span>
  );
};

// =================== SCORING HEADER ===================
export const ScoringHeader = ({ round, onBack, onComplete }) => {
  const [showRules, setShowRules] = useState(false);
  const gameType = round.games?.[0]?.type;
  const gameInfo = GAME_TYPES.find(g => g.value === gameType);
  // Only surface a notice when we used a non-primary source. Curated and
  // golfcourseapi pars are expected — no need to call them out.
  const parSourceNotice = {
    osm: 'Pars from OpenStreetMap (community data) — tap to correct if wrong',
  }[round.par_source];
  return (
    <>
      <div className="flex items-center gap-3 mb-4">
        <Button variant="ghost" size="icon" onClick={onBack} className="rounded-full" data-testid="back-from-scoring"><ArrowLeft className="w-5 h-5" /></Button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-md flex items-center justify-center text-[10px] font-black bg-primary/10 text-primary">{gameInfo?.icon}</span>
            <h2 className="font-serif text-base font-bold text-foreground">{gameInfo?.label}</h2>
            <button onClick={() => setShowRules(true)} className="w-6 h-6 rounded-full flex items-center justify-center hover:bg-muted transition-colors" data-testid="scoring-rules-btn">
              <HelpCircle className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
          <p className="text-xs text-muted-foreground">{round.course_name}{round.tee_name ? ` (${round.tee_name})` : ''} — {round.players.map(p => p.name).join(', ')}</p>
        </div>
        {round.status === 'active' && (
          <Button variant="outline" size="sm" onClick={onComplete} className="rounded-full text-xs gap-1" data-testid="complete-round-btn">
            <Check className="w-3 h-3" /> Finish
          </Button>
        )}
      </div>
      {parSourceNotice && (
        <div className="mb-4 px-3 py-2 rounded-lg bg-secondary/10 border border-secondary/20 text-[11px] text-muted-foreground" data-testid="par-source-notice">
          {parSourceNotice}
        </div>
      )}
      <GameRulesModal gameType={gameType} open={showRules} onClose={() => setShowRules(false)} />
    </>
  );
};

// =================== HOLE PILLS ===================
export const HolePills = ({ holes, activeHole, onSelect }) => (
  <div className="flex gap-1 overflow-x-auto pb-2 mb-4 scrollbar-none">
    {holes?.map((h, i) => {
      const n = i + 1;
      const done = h.scores && Object.keys(h.scores).length > 0;
      return (
        <button key={n} onClick={() => onSelect(n)} className={`flex-shrink-0 w-9 h-9 rounded-lg text-xs font-bold transition-all ${holeBtnClass(activeHole === n, done)}`} data-testid={`hole-btn-${n}`}>{n}</button>
      );
    })}
  </div>
);

// =================== SETTLEMENT ===================
export const Settlement = ({ results }) => {
  const s = results?.settlement;
  if (!s) return null;
  return (
    <div className="rounded-xl border-2 border-primary/30 p-4 bg-card mb-4" data-testid="settlement-card">
      <h3 className="font-semibold text-sm mb-3 text-foreground flex items-center gap-2">
        <Handshake className="w-4 h-4 text-primary" /> Standings
      </h3>
      <div className="space-y-1.5">
        {Object.entries(s.balances).sort(([,a],[,b]) => b - a).map(([name, bal]) => (
          <div key={name} className="flex items-center justify-between">
            <span className="text-sm text-foreground">{name}</span>
            <span className={`text-sm font-bold ${signColor(bal)}`}>
              {bal > 0 ? '+' : ''}${bal.toFixed(2)}
            </span>
          </div>
        ))}
      </div>
      {s.payouts?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-border space-y-2">
          <p className="text-[10px] font-bold uppercase text-muted-foreground">Payouts</p>
          {s.payouts.map((p) => (
            <div key={`${p.from}-${p.to}`} className="flex items-center gap-2 p-2 rounded-lg bg-muted/50 text-sm">
              <span className="font-medium text-foreground">{p.from}</span>
              <span className="text-xs text-muted-foreground">→</span>
              <span className="font-medium text-foreground">{p.to}</span>
              <span className="ml-auto font-black text-primary">${p.amount.toFixed(2)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// =================== LIVE SCORECARD ===================
export const LiveScorecard = ({ round }) => {
  const [expanded, setExpanded] = useState(false);
  const players = round.players;
  const holes = round.holes || [];
  if (!holes.length) return null;

  const getTotal = (name, start, end) => {
    let t = 0;
    for (let i = start; i < Math.min(end, holes.length); i++) {
      const s = holes[i]?.scores?.[name];
      if (s != null) t += s;
    }
    return t;
  };
  const parTotal = (start, end) => {
    let t = 0;
    for (let i = start; i < Math.min(end, holes.length); i++) t += (holes[i]?.par || 4);
    return t;
  };

  return (
    <div className="rounded-xl border border-border bg-card mb-4 overflow-hidden" data-testid="live-scorecard">
      <button onClick={() => setExpanded(!expanded)} className="w-full px-3 py-2 flex items-center justify-between text-xs font-bold uppercase text-muted-foreground hover:bg-muted/50">
        <span>Scorecard</span>
        <span>{expanded ? '−' : '+'}</span>
      </button>
      {expanded && (
        <div className="overflow-x-auto px-1 pb-2">
          <table className="w-full text-[10px]">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-1.5 py-1 font-bold text-muted-foreground sticky left-0 bg-card">Hole</th>
                {holes.slice(0, 9).map((_, i) => <th key={i} className="px-1 py-1 text-center font-bold text-muted-foreground">{i + 1}</th>)}
                <th className="px-1 py-1 text-center font-black text-foreground">OUT</th>
                {holes.slice(9, 18).map((_, i) => <th key={i + 9} className="px-1 py-1 text-center font-bold text-muted-foreground">{i + 10}</th>)}
                <th className="px-1 py-1 text-center font-black text-foreground">IN</th>
                <th className="px-1.5 py-1 text-center font-black text-primary">TOT</th>
              </tr>
              <tr className="border-b border-border bg-muted/30">
                <td className="text-left px-1.5 py-0.5 font-bold text-muted-foreground sticky left-0 bg-muted/30">Par</td>
                {holes.slice(0, 9).map((h, i) => <td key={i} className="px-1 py-0.5 text-center text-muted-foreground">{h.par || 4}</td>)}
                <td className="px-1 py-0.5 text-center font-bold text-foreground">{parTotal(0, 9)}</td>
                {holes.slice(9, 18).map((h, i) => <td key={i} className="px-1 py-0.5 text-center text-muted-foreground">{h.par || 4}</td>)}
                <td className="px-1 py-0.5 text-center font-bold text-foreground">{parTotal(9, 18)}</td>
                <td className="px-1.5 py-0.5 text-center font-bold text-primary">{parTotal(0, 18)}</td>
              </tr>
            </thead>
            <tbody>
              {players.map(p => (
                <tr key={p.name} className="border-b border-border last:border-0">
                  <td className="text-left px-1.5 py-1 font-semibold text-foreground truncate max-w-[60px] sticky left-0 bg-card">{p.name}</td>
                  {holes.slice(0, 9).map((h, i) => {
                    const s = h.scores?.[p.name];
                    const par = h.par || 4;
                    return <td key={i} className={`px-1 py-1 text-center font-bold ${scoreColor(s, par)}`}>{s ?? '-'}</td>;
                  })}
                  <td className="px-1 py-1 text-center font-black text-foreground">{getTotal(p.name, 0, 9) || '-'}</td>
                  {holes.slice(9, 18).map((h, i) => {
                    const s = h.scores?.[p.name];
                    const par = h.par || 4;
                    return <td key={i} className={`px-1 py-1 text-center font-bold ${scoreColor(s, par)}`}>{s ?? '-'}</td>;
                  })}
                  <td className="px-1 py-1 text-center font-black text-foreground">{getTotal(p.name, 9, 18) || '-'}</td>
                  <td className="px-1.5 py-1 text-center font-black text-primary">{getTotal(p.name, 0, 18) || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// =================== SCORE ROW ===================
// Helper: pre-populates each player's score with the hole's par so the user
// starts AT par and just adjusts up/down — instead of starting blank.
export const buildInitialScores = (round, holeIdx) => {
  const saved = round?.holes?.[holeIdx]?.scores || {};
  const par = round?.holes?.[holeIdx]?.par || 4;
  const init = {};
  for (const p of (round?.players || [])) {
    init[p.name] = saved[p.name] ?? par;
  }
  return init;
};

export const ScoreRow = ({ name, score, par, onChange }) => {
  const safeScore = (typeof score === 'number' && score > 0) ? score : par;
  return (
    <div className="flex items-center gap-3 mb-2">
      <span className="text-sm font-medium text-foreground w-20 truncate">{name}</span>
      <div className="flex items-center gap-2 flex-1">
        <button
          onClick={() => onChange(Math.max(1, safeScore - 1))}
          className="w-8 h-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-muted active:scale-95 transition-transform"
          data-testid={`score-minus-${name}`}
        >-</button>
        <Input
          type="number"
          inputMode="numeric"
          min="1"
          value={safeScore}
          onChange={e => { const v = Number(e.target.value); onChange(v < 1 ? 1 : v); }}
          className="w-14 h-8 text-center rounded-lg text-sm font-bold"
          data-testid={`score-input-${name}`}
        />
        <button
          onClick={() => onChange(safeScore + 1)}
          className="w-8 h-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-muted active:scale-95 transition-transform"
          data-testid={`score-plus-${name}`}
        >+</button>
      </div>
      <span className={`text-xs font-bold min-w-[28px] text-center ${signColor(par - safeScore)}`}>
        {relToPar(safeScore, par)}
      </span>
    </div>
  );
};

// =================== SHARED: BOTTOM SHEET / SIDE BET ===================
export const BottomSheet = ({ onClose, title, children }) => (
  <div className="fixed inset-0 z-[60] bg-black/50 flex items-end" onClick={onClose}>
    <div className="w-full max-w-lg mx-auto bg-card rounded-t-2xl p-5 pb-8 max-h-[70vh] overflow-y-auto" style={{ paddingBottom: 'calc(2rem + env(safe-area-inset-bottom))' }} onClick={e => e.stopPropagation()}>
      <h3 className="font-semibold text-foreground mb-3">{title}</h3>
      {children}
    </div>
  </div>
);

export const SideBetSheet = ({ players, onAdd, onClose, hole }) => (
  <BottomSheet onClose={onClose} title={`Side Bet — Hole ${hole}`}>
    {SIDE_BET_TYPES.map(sb => (
      <div key={sb.value} className="mb-3">
        <p className="text-xs font-bold text-muted-foreground mb-1">{sb.label} <span className="font-normal">— {sb.desc}</span></p>
        <div className="flex gap-1 mb-1">
          {players.map(p => (
            <button key={p.name} onClick={() => onAdd(sb.value, p.name)} className="px-3 py-1.5 rounded-full text-xs font-semibold border border-border bg-muted text-foreground hover:bg-primary hover:text-primary-foreground transition-colors" data-testid={`sidebet-${sb.value}-${p.name}`}>
              {p.name}
            </button>
          ))}
        </div>
      </div>
    ))}
  </BottomSheet>
);

// =================== GENERIC SCORING ===================
const GenericScoring = ({ round, onUpdate, renderHeader, renderExtra }) => {
  const [activeHole, setActiveHole] = useState(round.current_hole || 1);
  const [scores, setScores] = useState({});
  const [saving, setSaving] = useState(false);
  const [showSideBet, setShowSideBet] = useState(false);
  const players = round.players;
  const hole = round.holes?.[activeHole - 1] || {};

  useEffect(() => { setScores(buildInitialScores(round, activeHole - 1)); }, [activeHole, round]);

  const save = async () => {
    const missing = players.filter(p => scores[p.name] == null || scores[p.name] === 0);
    if (missing.length > 0) { toast.error(`Enter scores for: ${missing.map(p => p.name).join(', ')}`); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/betting/rounds/${round.id}/scores`, { hole_number: activeHole, scores });
      onUpdate();
      if (activeHole < 18) setActiveHole(activeHole + 1);
      toast.success(`Hole ${activeHole} saved`);
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const addSideBet = async (type, name) => {
    try { await axios.post(`${API}/betting/rounds/${round.id}/side-bet`, { bet_type: type, player_name: name, hole_number: activeHole, amount: 1 }); onUpdate(); toast.success(`${type} — ${name}`); } catch { toast.error('Failed'); }
    setShowSideBet(false);
  };

  return (
    <div>
      <LiveScorecard round={round} />
      {renderHeader && renderHeader(round, activeHole)}
      <HolePills holes={round.holes} activeHole={activeHole} onSelect={n => setActiveHole(n)} />

      {renderExtra && renderExtra(round, activeHole, scores, setScores, onUpdate)}

      <div className="rounded-xl border border-border p-4 bg-card mb-4" data-testid="score-entry">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-foreground flex items-center gap-2">Hole {activeHole} <ParEditor roundId={round.id} holeNumber={activeHole} par={hole.par || 4} onUpdated={onUpdate} /></h3>
          <button onClick={() => setShowSideBet(true)} className="px-2 py-1 rounded-full text-[10px] font-bold bg-primary/10 text-primary" data-testid="side-bet-btn">+ Side Bet</button>
        </div>
        {players.map(p => (
          <ScoreRow key={p.name} name={p.name} score={scores[p.name]} par={hole.par || 4} onChange={v => setScores({ ...scores, [p.name]: v })} />
        ))}
        <Button onClick={save} disabled={saving} className="w-full mt-3 rounded-lg h-10" data-testid="save-scores-btn">
          {saving ? 'Saving...' : `Save Hole ${activeHole}`}
        </Button>
      </div>

      <Settlement results={round.results || {}} />

      {showSideBet && <SideBetSheet players={players} onAdd={addSideBet} onClose={() => setShowSideBet(false)} hole={activeHole} />}
    </div>
  );
};

// =================== NASSAU SCORING ===================
const SEG_LABELS = { front_9: 'Front 9', back_9: 'Back 9', overall: 'Overall' };
const partToSeg = (part) => PART_TO_SEG[part] || 'overall';

const segStatus = (seg) => {
  if (!seg) return '—';
  if (!seg.leader) return 'AS';
  return `${seg.leader.split(' ')[0]} ${Math.abs(seg.p1_up)}↑`;
};

const NassauPairwiseCard = ({ pairwise, stake }) => {
  if (!pairwise?.length) return null;
  return (
    <div className="space-y-2 mb-4" data-testid="nassau-pairwise-card">
      {pairwise.map((pw, i) => (
        <div key={`${pw.p1}-${pw.p2}`} className="rounded-xl border border-border bg-card p-3" data-testid={`pair-card-${i}`}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-black text-foreground">{pw.p1} <span className="text-muted-foreground font-semibold">vs</span> {pw.p2}</p>
            <span className={`text-xs font-black ${signColor(pw.net)}`} data-testid={`pair-net-${i}`}>
              {pairNetLabel(pw)}
              <span className="block text-[8px] font-semibold text-muted-foreground text-right">if it ends now</span>
            </span>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {['front_9', 'back_9', 'overall'].map(k => (
              <div key={k} className="rounded-lg bg-muted/50 p-1.5 text-center">
                <p className="text-[9px] font-bold uppercase text-muted-foreground">{SEG_LABELS[k]}</p>
                <p className="text-[11px] font-black text-foreground">{segStatus(pw.segments?.[k])}</p>
              </div>
            ))}
          </div>
          {pw.presses?.length > 0 && (
            <div className="mt-2 pt-2 border-t border-border space-y-1">
              {pw.presses.map((pr, j) => (
                <div key={j} className="flex items-center justify-between text-[11px]" data-testid={`pair-press-${i}-${j}`}>
                  <span className="text-muted-foreground">
                    <span className="font-black text-secondary">PRESS</span> {SEG_LABELS[partToSeg(pr.part)]} · from #{pr.from_hole} · by {pr.initiator?.split(' ')[0]}
                  </span>
                  <span className="font-black text-foreground">
                    {pr.leader ? `${pr.leader.split(' ')[0]} ${Math.abs(pr.p1_up)}↑ · $${stake}` : 'AS'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const PressSheet = ({ players, activeHole, stake, onPress, onClose }) => {
  const isMulti = players.length > 2;
  const [initiator, setInitiator] = useState(null);
  const [target, setTarget] = useState(null);
  const ready = initiator && (!isMulti || target);
  const pill = (active) => `px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${active ? 'bg-secondary text-secondary-foreground border-secondary' : 'border-border bg-muted text-foreground'}`;
  return (
    <BottomSheet onClose={onClose} title={`Press — Hole ${activeHole}`}>
      <p className="text-xs text-muted-foreground mb-4" data-testid="press-explainer">
        A press starts a <span className="font-bold text-foreground">new bet</span> at the same stake (${stake}) covering hole {activeHole} through the end of the segment you pick.{isMulti ? ' It only counts between the two players you select.' : ''}
      </p>
      <p className="text-[10px] font-bold uppercase text-muted-foreground mb-1.5">1. Who is pressing?</p>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {players.map(p => (
          <button key={p.name} onClick={() => { setInitiator(p.name); if (target === p.name) setTarget(null); }} className={pill(initiator === p.name)} data-testid={`press-initiator-${p.name}`}>
            {p.name}
          </button>
        ))}
      </div>
      {isMulti && (
        <>
          <p className={`text-[10px] font-bold uppercase mb-1.5 ${initiator ? 'text-muted-foreground' : 'text-muted-foreground/40'}`}>2. Pressing whom?</p>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {players.filter(p => p.name !== initiator).map(p => (
              <button key={p.name} disabled={!initiator} onClick={() => setTarget(p.name)} className={`${pill(target === p.name)} ${!initiator ? 'opacity-40' : ''}`} data-testid={`press-target-${p.name}`}>
                {p.name}
              </button>
            ))}
          </div>
        </>
      )}
      <p className={`text-[10px] font-bold uppercase mb-1.5 ${ready ? 'text-muted-foreground' : 'text-muted-foreground/40'}`}>{isMulti ? '3' : '2'}. Which bet to press?</p>
      <div className="flex gap-1.5">
        {[['front', 'Front 9'], ['back', 'Back 9'], ['overall', 'Overall 18']].map(([part, label]) => (
          <button
            key={part}
            disabled={!ready}
            onClick={() => onPress(initiator, isMulti ? target : null, part)}
            className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold border transition-colors ${ready ? 'border-secondary bg-secondary/10 text-secondary hover:bg-secondary hover:text-secondary-foreground' : 'border-border bg-muted text-muted-foreground/40'}`}
            data-testid={`press-part-${part}`}
          >
            {label}
          </button>
        ))}
      </div>
    </BottomSheet>
  );
};

const NassauScoring = ({ round, onUpdate }) => {
  const [activeHole, setActiveHole] = useState(round.current_hole || 1);
  const [scores, setScores] = useState({});
  const [saving, setSaving] = useState(false);
  const [showPress, setShowPress] = useState(false);
  const [showSideBet, setShowSideBet] = useState(false);
  const players = round.players;
  const hole = round.holes?.[activeHole - 1] || {};
  const results = round.results || {};
  const nassauStake = round.games?.find(g => g.type === 'nassau')?.stake ?? 1;

  useEffect(() => { setScores(buildInitialScores(round, activeHole - 1)); }, [activeHole, round]);

  const save = async () => {
    const missing = players.filter(p => scores[p.name] == null || scores[p.name] === 0);
    if (missing.length > 0) { toast.error(`Enter scores for: ${missing.map(p => p.name).join(', ')}`); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/betting/rounds/${round.id}/scores`, { hole_number: activeHole, scores });
      onUpdate();
      if (activeHole < 18) setActiveHole(activeHole + 1);
      toast.success(`Hole ${activeHole} saved`);
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const addPress = async (initiator, target, part) => {
    try {
      const res = await axios.post(`${API}/betting/rounds/${round.id}/press`, { initiator, target, hole_number: activeHole, nassau_part: part });
      onUpdate();
      toast.success(res.data?.message || 'Press is ON!', { duration: 4500 });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add press'); }
    setShowPress(false);
  };

  const addSideBet = async (type, name) => {
    try { await axios.post(`${API}/betting/rounds/${round.id}/side-bet`, { bet_type: type, player_name: name, hole_number: activeHole, amount: 1 }); onUpdate(); toast.success(`${type} — ${name}`); } catch { toast.error('Failed'); }
    setShowSideBet(false);
  };

  const matchStatus = (holeSet) => {
    const wins = {};
    players.forEach(p => wins[p.name] = 0);
    holeSet.forEach(h => {
      const s = h.scores || {};
      const valid = players.map(p => ({ name: p.name, score: s[p.name] })).filter(x => x.score != null);
      if (valid.length < 2) return;
      const min = Math.min(...valid.map(v => v.score));
      const w = valid.filter(v => v.score === min);
      if (w.length === 1) wins[w[0].name]++;
    });
    return wins;
  };

  const front = matchStatus(round.holes?.slice(0, 9) || []);
  const back = matchStatus(round.holes?.slice(9, 18) || []);

  return (
    <div>
      <LiveScorecard round={round} />
      {results.nassau?.pairwise?.length > 0 ? (
        <NassauPairwiseCard pairwise={results.nassau.pairwise} stake={nassauStake} />
      ) : (
        <div className="grid grid-cols-3 gap-2 mb-4">
          {['Front 9', 'Back 9', 'Overall'].map((label, idx) => {
            const wins = [front, back][idx] ?? matchStatus(round.holes || []);
            const sorted = Object.entries(wins).sort(([,a],[,b]) => b - a);
            const leader = sorted[0];
            return (
              <div key={label} className="rounded-lg p-2 text-center bg-muted/50">
                <p className="text-[9px] font-bold uppercase text-muted-foreground">{label}</p>
                <p className="text-sm font-black text-foreground">{leader?.[0]?.split(' ')[0] || '—'}</p>
                <p className="text-[10px] text-primary font-bold">{leader?.[1] || 0} holes</p>
              </div>
            );
          })}
        </div>
      )}

      <HolePills holes={round.holes} activeHole={activeHole} onSelect={n => setActiveHole(n)} />

      <div className="rounded-xl border border-border p-4 bg-card mb-4" data-testid="score-entry">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-foreground flex items-center gap-2">Hole {activeHole} <ParEditor roundId={round.id} holeNumber={activeHole} par={hole.par || 4} onUpdated={onUpdate} /></h3>
          <div className="flex gap-1.5">
            <button onClick={() => setShowPress(true)} className="px-2 py-1 rounded-full text-[10px] font-bold bg-secondary/15 text-secondary" data-testid="press-btn">Press</button>
            <button onClick={() => setShowSideBet(true)} className="px-2 py-1 rounded-full text-[10px] font-bold bg-primary/10 text-primary" data-testid="side-bet-btn">+ Side Bet</button>
          </div>
        </div>
        {players.map(p => (
          <ScoreRow key={p.name} name={p.name} score={scores[p.name]} par={hole.par || 4} onChange={v => setScores({ ...scores, [p.name]: v })} />
        ))}
        <Button onClick={save} disabled={saving} className="w-full mt-3 rounded-lg h-10" data-testid="save-scores-btn">
          {saving ? 'Saving...' : `Save Hole ${activeHole}`}
        </Button>
      </div>

      {!results.nassau?.pairwise?.length && round.presses?.length > 0 && (
        <div className="rounded-xl border border-border p-3 bg-card mb-4 text-xs space-y-1" data-testid="presses-list">
          <p className="font-bold text-muted-foreground uppercase text-[10px]">Presses</p>
          {round.presses.map((pr) => <p key={pr.id || `${pr.initiator}-${pr.target}-${pr.nassau_part}-${pr.hole_number}`} className="text-muted-foreground">{pr.initiator} pressed {pr.target ? `${pr.target} on ` : ''}{pr.nassau_part} on #{pr.hole_number}</p>)}
        </div>
      )}

      <Settlement results={results} />

      {showPress && <PressSheet players={players} activeHole={activeHole} stake={nassauStake} onPress={addPress} onClose={() => setShowPress(false)} />}

      {showSideBet && <SideBetSheet players={players} onAdd={addSideBet} onClose={() => setShowSideBet(false)} hole={activeHole} />}
    </div>
  );
};

// =================== SKINS ===================
const SkinsScoring = ({ round, onUpdate }) => {
  const players = round.players;
  const results = round.results || {};
  const skins = results.skins || {};

  const renderHeader = () => (
    <>
      <div className="flex gap-2 mb-4 overflow-x-auto">
        {players.map(p => (
          <div key={p.name} className="flex-1 min-w-[80px] rounded-lg p-2 text-center bg-muted/50">
            <p className="text-[10px] font-bold uppercase text-muted-foreground truncate">{p.name}</p>
            <p className="text-xl font-black text-foreground">{skins.skins?.[p.name] || 0}</p>
            <p className="text-[9px] text-primary font-bold">skins</p>
          </div>
        ))}
        {(skins.carryover || 0) > 0 && (
          <div className="flex-shrink-0 w-16 rounded-lg p-2 text-center bg-secondary/10">
            <p className="text-[10px] font-bold uppercase text-secondary">Carry</p>
            <p className="text-xl font-black text-secondary">{skins.carryover}</p>
          </div>
        )}
      </div>
      <div className="flex gap-1 flex-wrap mb-3">
        {round.holes?.map((h, i) => {
          const s = h.scores || {};
          const valid = players.map(p => ({ name: p.name, score: s[p.name] })).filter(x => x.score != null);
          const min = valid.length ? Math.min(...valid.map(v => v.score)) : null;
          const winners = valid.filter(v => v.score === min);
          const won = winners.length === 1;
          return (
            <div key={i} className={`w-7 h-7 rounded text-[9px] font-bold flex items-center justify-center ${holeChipClass(won ? 0 : -1, Object.keys(s).length > 0)}`} title={won ? `${winners[0].name} won` : 'carry'}>
              {won ? winners[0].name[0] : i + 1}
            </div>
          );
        })}
      </div>
    </>
  );

  return <GenericScoring round={round} onUpdate={onUpdate} renderHeader={renderHeader} />;
};

// =================== WOLF ===================
const WolfOrderPicker = ({ round, onUpdate }) => {
  const [picked, setPicked] = useState([]);
  const names = round.players.map(p => p.name);

  const submit = async (order) => {
    try {
      await axios.put(`${API}/betting/rounds/${round.id}/wolf-order`, { order });
      onUpdate();
      toast.success(`Tee order set: ${order.join(' → ')}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to set order'); }
  };

  return (
    <div className="rounded-xl border-2 border-primary/40 p-4 bg-card mb-4" data-testid="wolf-order-picker">
      <p className="text-xs font-bold uppercase text-muted-foreground mb-1">Set Tee Order</p>
      <p className="text-[11px] text-muted-foreground mb-3">Tap players in the order they tee off on hole 1. The Wolf rotates in this order all round.</p>
      <div className="flex flex-wrap gap-2 mb-3">
        {names.map(n => {
          const pos = picked.indexOf(n);
          return (
            <button
              key={n}
              onClick={() => pos === -1 && setPicked([...picked, n])}
              disabled={pos !== -1}
              className={`px-3 py-2 rounded-full text-xs font-semibold border transition-colors flex items-center gap-1.5 ${pos !== -1 ? 'bg-primary text-primary-foreground border-primary' : 'border-border bg-muted text-foreground hover:border-primary'}`}
              data-testid={`wolf-order-pick-${n}`}
            >
              {pos !== -1 && <span className="w-4 h-4 rounded-full bg-primary-foreground text-primary text-[10px] font-black flex items-center justify-center">{pos + 1}</span>}
              {n}
            </button>
          );
        })}
      </div>
      <div className="flex gap-2">
        <button onClick={() => setPicked([])} className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-border text-muted-foreground hover:text-foreground" data-testid="wolf-order-reset">
          Reset
        </button>
        <button
          onClick={() => submit(picked)}
          disabled={picked.length !== names.length}
          className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${picked.length === names.length ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-muted text-muted-foreground/40'}`}
          data-testid="wolf-order-confirm"
        >
          Confirm Order
        </button>
        <button onClick={() => submit(names)} className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-border text-muted-foreground hover:text-foreground" data-testid="wolf-order-default">
          Keep entry order
        </button>
      </div>
    </div>
  );
};

const WolfScoring = ({ round, onUpdate }) => {
  const basePlayers = round.players;
  const players = round.wolf_order?.length === basePlayers.length
    ? round.wolf_order.map(n => basePlayers.find(p => p.name === n)).filter(Boolean)
    : basePlayers;
  const results = round.results || {};
  const [activeHole, setActiveHole] = useState(round.current_hole || 1);
  const [scores, setScores] = useState({});
  const [saving, setSaving] = useState(false);
  const hole = round.holes?.[activeHole - 1] || {};
  const rawWolfData = hole?.wolf_data;
  // A wolf pick is only "set" if the wolf either went lone or picked a partner.
  // Clearing the pick stores {partner_id: null, lone_wolf: false} so we treat that as unset.
  const wolfData = (rawWolfData && (rawWolfData.lone_wolf || rawWolfData.partner_id)) ? rawWolfData : null;
  const wolfIdx = (activeHole - 1) % players.length;
  const currentWolf = players[wolfIdx]?.name;

  useEffect(() => { setScores(buildInitialScores(round, activeHole - 1)); }, [activeHole, round]);

  const save = async () => {
    if (!wolfData) {
      toast.error(`${currentWolf} must pick a partner or go Lone Wolf before saving this hole.`);
      return;
    }
    const missing = players.filter(p => scores[p.name] == null || scores[p.name] === 0);
    if (missing.length > 0) { toast.error(`Enter scores for: ${missing.map(p => p.name).join(', ')}`); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/betting/rounds/${round.id}/scores`, { hole_number: activeHole, scores });
      onUpdate();
      if (activeHole < 18) setActiveHole(activeHole + 1);
      toast.success(`Hole ${activeHole} saved`);
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const setWolf = async (partner, lone) => {
    try {
      await axios.put(`${API}/betting/rounds/${round.id}/wolf`, { hole_number: activeHole, wolf_id: currentWolf, partner_id: partner, lone_wolf: lone });
      onUpdate();
    } catch { toast.error('Failed'); }
  };

  const clearWolfPick = async () => {
    try {
      await axios.put(`${API}/betting/rounds/${round.id}/wolf`, { hole_number: activeHole, wolf_id: currentWolf, partner_id: null, lone_wolf: false });
      onUpdate();
    } catch { toast.error('Failed'); }
  };

  return (
    <div>
      <LiveScorecard round={round} />
      {!round.wolf_order?.length && <WolfOrderPicker round={round} onUpdate={onUpdate} />}
      <div className="flex gap-2 mb-4">
        {players.map((p, i) => (
          <div key={p.name} className={`flex-1 rounded-lg p-2 text-center ${wolfIdx === i ? 'bg-primary/10 border-2 border-primary' : 'bg-muted/50'}`}>
            <p className="text-[10px] font-bold uppercase text-muted-foreground">{wolfIdx === i ? 'WOLF' : ''}</p>
            <p className={`text-sm font-bold ${wolfIdx === i ? 'text-primary' : 'text-foreground'}`}>{p.name}</p>
            <p className="text-xs font-bold text-muted-foreground">${results.wolf?.balances?.[p.name]?.toFixed(0) || 0}</p>
          </div>
        ))}
      </div>

      <HolePills holes={round.holes} activeHole={activeHole} onSelect={n => setActiveHole(n)} />

      <div className={`rounded-xl p-4 bg-card mb-4 transition-colors ${wolfData ? 'border border-border' : 'border-2 border-amber-500 bg-amber-50 dark:bg-amber-950/20'}`} data-testid="wolf-pick-card">
        <p className="text-xs font-bold uppercase text-muted-foreground mb-2">Wolf: <span className="text-primary">{currentWolf}</span></p>
        {wolfData ? (
          <div className="flex items-center justify-between">
            <p className="text-sm text-foreground" data-testid="wolf-pick-display">
              {wolfData.lone_wolf
                ? <><span className="font-bold text-secondary">{currentWolf}</span> goes <span className="font-black text-secondary">LONE WOLF (2x)</span></>
                : <><span className="font-bold text-primary">{currentWolf}</span> picks <span className="font-bold text-primary">{wolfData.partner_id}</span> as partner</>}
            </p>
            <button onClick={clearWolfPick} className="text-xs text-muted-foreground underline hover:text-foreground" data-testid="wolf-change-pick">
              Change
            </button>
          </div>
        ) : (
          <>
            <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-2 uppercase tracking-wide">
              ⚠️ Pick a partner or go Lone Wolf to enable scoring
            </p>
            <div className="flex flex-wrap gap-2">
              {players.filter(p => p.name !== currentWolf).map(p => (
                <button key={p.name} onClick={() => setWolf(p.name, false)} className="px-4 py-2 rounded-full text-xs font-semibold border border-border bg-muted text-foreground hover:bg-primary hover:text-primary-foreground transition-colors" data-testid={`wolf-pick-${p.name}`}>
                  {p.name}
                </button>
              ))}
              <button onClick={() => setWolf(null, true)} className="px-4 py-2 rounded-full text-xs font-black border-2 border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground transition-colors" data-testid="wolf-lone-btn">
                LONE WOLF (2x)
              </button>
            </div>
          </>
        )}
      </div>

      <div className="rounded-xl border border-border p-4 bg-card mb-4" data-testid="wolf-score-entry">
        <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">Hole {activeHole} <ParEditor roundId={round.id} holeNumber={activeHole} par={hole.par || 4} onUpdated={onUpdate} /></h3>
        {players.map(p => (
          <ScoreRow key={p.name} name={p.name} score={scores[p.name]} par={hole.par || 4} onChange={v => setScores({ ...scores, [p.name]: v })} />
        ))}
        <Button onClick={save} disabled={saving || !wolfData} className="w-full mt-3 rounded-lg h-10" data-testid="save-scores-btn">
          {wolfSaveLabel(saving, wolfData, activeHole)}
        </Button>
      </div>

      <Settlement results={results} />
    </div>
  );
};

// =================== BBB ===================
const BBBScoring = ({ round, onUpdate }) => {
  const players = round.players;
  const results = round.results || {};
  const bbbResults = results.bingo_bango_bongo || {};
  const [activeHole, setActiveHole] = useState(round.current_hole || 1);
  const [scores, setScores] = useState({});
  const [saving, setSaving] = useState(false);
  const hole = round.holes?.[activeHole - 1] || {};
  const bbb = hole?.bbb_data;

  useEffect(() => { setScores(buildInitialScores(round, activeHole - 1)); }, [activeHole, round]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/betting/rounds/${round.id}/scores`, { hole_number: activeHole, scores });
      onUpdate();
      if (activeHole < 18) setActiveHole(activeHole + 1);
      toast.success(`Hole ${activeHole} saved`);
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const setBBB = async (type, name) => {
    try {
      const c = bbb || {};
      await axios.put(`${API}/betting/rounds/${round.id}/bbb`, {
        hole_number: activeHole,
        bingo: type === 'bingo' ? name : c.bingo,
        bango: type === 'bango' ? name : c.bango,
        bongo: type === 'bongo' ? name : c.bongo,
      });
      onUpdate();
    } catch { toast.error('Failed'); }
  };

  const pts = [
    { key: 'bingo', label: 'Bingo', desc: 'First on green', icon: <Target className="w-4 h-4" /> },
    { key: 'bango', label: 'Bango', desc: 'Closest to pin', icon: <Crosshair className="w-4 h-4" /> },
    { key: 'bongo', label: 'Bongo', desc: 'First to hole out', icon: <Check className="w-4 h-4" /> },
  ];

  return (
    <div>
      <LiveScorecard round={round} />
      <div className="flex gap-2 mb-4">
        {players.map(p => (
          <div key={p.name} className="flex-1 rounded-lg p-2 text-center bg-muted/50">
            <p className="text-[10px] font-bold uppercase text-muted-foreground truncate">{p.name}</p>
            <p className="text-xl font-black text-foreground">{bbbResults.points?.[p.name] || 0}</p>
            <p className="text-[9px] text-primary font-bold">pts</p>
          </div>
        ))}
      </div>

      <HolePills holes={round.holes} activeHole={activeHole} onSelect={n => setActiveHole(n)} />

      <div className="rounded-xl border border-border p-4 bg-card mb-4" data-testid="bbb-entry">
        <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">Hole {activeHole} <ParEditor roundId={round.id} holeNumber={activeHole} par={hole.par || 4} onUpdated={onUpdate} /></h3>
        {pts.map(pt => (
          <div key={pt.key} className="mb-3">
            <div className="flex items-center gap-2 mb-1">
              {pt.icon}
              <span className="text-xs font-bold text-muted-foreground">{pt.label}</span>
              <span className="text-[10px] text-muted-foreground">— {pt.desc}</span>
            </div>
            {bbb?.[pt.key] ? (
              <span className="px-3 py-1 rounded-full text-xs font-bold bg-primary/10 text-primary">{bbb[pt.key]}</span>
            ) : (
              <div className="flex gap-1.5">
                {players.map(p => (
                  <button key={p.name} onClick={() => setBBB(pt.key, p.name)} className="px-3 py-1 rounded-full text-xs font-semibold border border-border bg-muted text-foreground hover:bg-primary hover:text-primary-foreground transition-colors" data-testid={`bbb-${pt.key}-${p.name}`}>
                    {p.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-border p-4 bg-card mb-4">
        <h3 className="text-xs font-bold uppercase text-muted-foreground mb-2">Scores (optional)</h3>
        {players.map(p => (
          <ScoreRow key={p.name} name={p.name} score={scores[p.name]} par={hole.par || 4} onChange={v => setScores({ ...scores, [p.name]: v })} />
        ))}
        <Button onClick={save} disabled={saving} variant="outline" className="w-full mt-3 rounded-lg h-9 text-xs" data-testid="save-scores-btn">Save Scores</Button>
      </div>

      <Settlement results={results} />
    </div>
  );
};

// =================== MATCH PLAY ===================
const MatchPlayScoring = ({ round, onUpdate }) => {
  const players = round.players;
  const results = round.results || {};
  const mp = results.match_play || {};
  const p1 = players[0]?.name;
  const p2 = players[1]?.name;
  const w1 = mp.wins?.[p1] || 0;
  const w2 = mp.wins?.[p2] || 0;
  const diff = w1 - w2;
  const thru = round.holes?.filter(h => h.scores && Object.keys(h.scores).length > 0).length || 0;
  const statusText = matchStatusText(diff, p1, p2);

  const renderHeader = () => (
    <>
      <div className="rounded-xl p-4 mb-4 text-center bg-muted/50">
        <p className="text-xl font-black text-foreground">{statusText}</p>
        <p className="text-xs text-muted-foreground">Thru {thru} holes</p>
        <div className="flex justify-center gap-8 mt-2">
          <div><p className="text-sm font-bold text-foreground">{p1}</p><p className="text-lg font-black text-primary">{w1}</p></div>
          <div><p className="text-sm font-bold text-foreground">{p2}</p><p className="text-lg font-black text-primary">{w2}</p></div>
        </div>
      </div>
      <div className="flex gap-0.5 mb-4 flex-wrap">
        {round.holes?.map((h, i) => {
          const s = h.scores || {};
          if (!s[p1] && !s[p2]) return <div key={i} className="w-6 h-6 rounded text-[9px] flex items-center justify-center bg-muted/30 text-muted-foreground/30">{i+1}</div>;
          const winner = lowerOf(s, p1, p2);
          return (
            <div key={i} className={`w-6 h-6 rounded text-[9px] font-bold flex items-center justify-center ${holeChipClass([p1, p2].indexOf(winner), true)}`}>
              {winner ? winner[0] : '½'}
            </div>
          );
        })}
      </div>
    </>
  );

  return <GenericScoring round={round} onUpdate={onUpdate} renderHeader={renderHeader} />;
};

// =================== STABLEFORD ===================
const StablefordScoring = ({ round, onUpdate }) => {
  const players = round.players;
  const results = round.results || {};
  const stab = results.stableford || {};

  const renderHeader = () => (
    <div className="flex gap-2 mb-4">
      {[...players].sort((a,b) => (stab.points?.[b.name] || 0) - (stab.points?.[a.name] || 0)).map((p, i) => (
        <div key={p.name} className={`flex-1 rounded-lg p-2 text-center ${i === 0 ? 'bg-primary/10 border border-primary/30' : 'bg-muted/50'}`}>
          <p className="text-[10px] font-bold uppercase text-muted-foreground truncate">{p.name}</p>
          <p className="text-xl font-black text-foreground">{stab.points?.[p.name] || 0}</p>
          <p className="text-[9px] text-primary font-bold">pts</p>
        </div>
      ))}
    </div>
  );

  return <GenericScoring round={round} onUpdate={onUpdate} renderHeader={renderHeader} />;
};

// =================== HAMMER ===================
const HammerScoring = ({ round, onUpdate }) => {
  const players = round.players;
  const results = round.results || {};
  const hammer = results.hammer || {};
  const baseStake = round.games?.find(g => g.type === 'hammer')?.stake ?? 1;

  const renderHeader = () => (
    <div className="flex gap-2 mb-4">
      {players.map(p => {
        const bal = hammer.balances?.[p.name] || 0;
        return (
          <div key={p.name} className="flex-1 rounded-lg p-2 text-center bg-muted/50">
            <p className="text-[10px] font-bold uppercase text-muted-foreground truncate">{p.name}</p>
            <p className="text-xl font-black text-foreground">{bal < 0 ? '-' : ''}${Math.abs(bal).toFixed(0)}</p>
          </div>
        );
      })}
    </div>
  );

  const renderExtra = (rd, activeHole, scores, setScores, onUpd) => {
    const h = rd.holes?.[activeHole - 1] || {};
    const hd = h.hammer_data;
    const curValue = hd?.value || baseStake;
    const lastHammerer = hd?.hammered_by;

    const throwHammer = async (name) => {
      try {
        await axios.put(`${API}/betting/rounds/${rd.id}/hammer`, { hole_number: activeHole, hammered_by: name, value: curValue * 2 });
        onUpd();
        toast.success(`${name} hammers! Hole ${activeHole} is now worth $${curValue * 2}`, { duration: 4000 });
      } catch { toast.error('Failed to hammer'); }
    };

    return (
      <div className="rounded-xl border-2 border-secondary/40 p-3 bg-card mb-4" data-testid="hammer-control">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-bold uppercase text-muted-foreground flex items-center gap-1.5">
            <Hammer className="w-3.5 h-3.5 text-secondary" /> Hole {activeHole} Value
          </p>
          <p className="text-lg font-black text-secondary" data-testid="hammer-value">${curValue}</p>
        </div>
        {lastHammerer && (
          <p className="text-[11px] text-muted-foreground mb-2" data-testid="hammer-last">
            Last hammer by <span className="font-bold text-foreground">{lastHammerer}</span> — opponent can hammer back to double again
          </p>
        )}
        <div className="flex gap-2">
          {players.map(p => (
            <button
              key={p.name}
              disabled={lastHammerer === p.name}
              onClick={() => throwHammer(p.name)}
              className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold border transition-colors ${lastHammerer === p.name ? 'border-border bg-muted text-muted-foreground/40' : 'border-secondary bg-secondary/10 text-secondary hover:bg-secondary hover:text-secondary-foreground'}`}
              data-testid={`hammer-btn-${p.name}`}
            >
              {p.name} hammers → ${curValue * 2}
            </button>
          ))}
        </div>
      </div>
    );
  };

  return <GenericScoring round={round} onUpdate={onUpdate} renderHeader={renderHeader} renderExtra={renderExtra} />;
};

// =================== DOTS ===================
const DotsScoring = ({ round, onUpdate }) => {
  const players = round.players;
  const results = round.results || {};
  const dots = results.dots || {};

  const renderHeader = () => (
    <div className="flex gap-2 mb-4">
      {players.map(p => (
        <div key={p.name} className="flex-1 rounded-lg p-2 text-center bg-muted/50">
          <p className="text-[10px] font-bold uppercase text-muted-foreground truncate">{p.name}</p>
          <p className="text-xl font-black text-foreground">{dots.dots?.[p.name] || 0}</p>
          <p className="text-[9px] text-primary font-bold">dots</p>
        </div>
      ))}
    </div>
  );

  return <GenericScoring round={round} onUpdate={onUpdate} renderHeader={renderHeader} />;
};

// =================== TRASH ===================
const TrashScoring = ({ round, onUpdate }) => {
  const players = round.players;
  const results = round.results || {};
  const trash = results.trash || {};
  const [activeHole, setActiveHole] = useState(round.current_hole || 1);
  const [showSideBet, setShowSideBet] = useState(false);

  const addSideBet = async (type, name) => {
    try { await axios.post(`${API}/betting/rounds/${round.id}/side-bet`, { bet_type: type, player_name: name, hole_number: activeHole, amount: 1 }); onUpdate(); toast.success(`${type} — ${name}`); } catch { toast.error('Failed'); }
    setShowSideBet(false);
  };

  return (
    <div>
      <div className="flex gap-2 mb-4 overflow-x-auto">
        {players.map(p => (
          <div key={p.name} className="flex-1 min-w-[80px] rounded-lg p-2 text-center bg-muted/50">
            <p className="text-[10px] font-bold uppercase text-muted-foreground truncate">{p.name}</p>
            <p className="text-xl font-black text-foreground">{trash.junk_counts?.[p.name] || 0}</p>
            <p className="text-[9px] text-primary font-bold">junk</p>
          </div>
        ))}
      </div>

      {trash.junk_details && Object.keys(trash.junk_details).length > 0 && (
        <div className="rounded-xl border border-border p-3 bg-card mb-4 text-xs space-y-1">
          <p className="font-bold text-muted-foreground uppercase text-[10px]">Junk Collected</p>
          {Object.entries(trash.junk_details).map(([type, items]) => (
            <p key={type} className="text-muted-foreground capitalize">{type}: {items.map(i => `${i.player} (#${i.hole})`).join(', ')}</p>
          ))}
        </div>
      )}

      <HolePills holes={round.holes} activeHole={activeHole} onSelect={n => setActiveHole(n)} />

      <div className="rounded-xl border border-border p-4 bg-card mb-4" data-testid="trash-entry">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-foreground">Hole {activeHole}</h3>
          <button onClick={() => setShowSideBet(true)} className="px-3 py-1.5 rounded-full text-xs font-bold bg-primary text-primary-foreground" data-testid="add-junk-btn">+ Add Junk</button>
        </div>
        <p className="text-xs text-muted-foreground">Tap "Add Junk" to award greenies, sandies, barkies and more to players on this hole.</p>
      </div>

      <Settlement results={results} />

      {showSideBet && <SideBetSheet players={players} onAdd={addSideBet} onClose={() => setShowSideBet(false)} hole={activeHole} />}
    </div>
  );
};

// =================== BEST BALL ===================
const BestBallScoring = ({ round, onUpdate }) => {
  const results = round.results || {};
  const bb = results.best_ball || {};
  const teams = round.teams || [];

  const renderHeader = () => (
    <div className="space-y-2 mb-4">
      {teams.map(t => (
        <div key={t.name} className="flex items-center gap-3 rounded-lg p-3 bg-muted/50">
          <div className="flex-1">
            <p className="text-xs font-bold uppercase text-muted-foreground">{t.name}</p>
            <p className="text-sm font-medium text-foreground">{t.player_names?.join(', ')}</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-black text-foreground">{bb.team_totals?.[t.name] || 0}</p>
            <p className="text-[9px] text-primary font-bold">{bb.team_wins?.[t.name] || 0} holes won</p>
          </div>
        </div>
      ))}
    </div>
  );

  return <GenericScoring round={round} onUpdate={onUpdate} renderHeader={renderHeader} />;
};

// =================== SCRAMBLE ===================
const ScrambleScoring = ({ round, onUpdate }) => {
  const results = round.results || {};
  const scr = results.scramble || {};
  const teams = round.teams || [];

  const renderHeader = () => (
    <div className="space-y-2 mb-4">
      {teams.map(t => (
        <div key={t.name} className="flex items-center gap-3 rounded-lg p-3 bg-muted/50">
          <div className="flex-1">
            <p className="text-xs font-bold uppercase text-muted-foreground">{t.name}</p>
            <p className="text-sm font-medium text-foreground">{t.player_names?.join(', ')}</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-black text-foreground">{scr.team_totals?.[t.name] || 0}</p>
            <p className="text-[9px] text-primary font-bold">{scr.team_wins?.[t.name] || 0} holes won</p>
          </div>
        </div>
      ))}
    </div>
  );

  return <GenericScoring round={round} onUpdate={onUpdate} renderHeader={renderHeader} />;
};

// =================== ROUND SUMMARY ===================
export const RoundSummary = ({ round, onBack }) => {
  const [savingScore, setSavingScore] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [downloadingCard, setDownloadingCard] = useState(false);
  const results = round.results || round.final_results || {};
  const gameType = round.games?.[0]?.type;
  const gameLabel = GAME_TYPES.find(g => g.value === gameType)?.label || gameType;
  const cardUrl = `${API}/betting/rounds/${round.id}/share-card.png`;

  const saveToScores = async () => {
    setSavingScore(true);
    try { await axios.post(`${API}/betting/rounds/${round.id}/save-to-scores`); toast.success('Score saved to your scorecard!'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSavingScore(false); }
  };

  const shareToFeed = async () => {
    setSharing(true);
    try { await axios.post(`${API}/betting/rounds/${round.id}/share-to-feed`); toast.success('Shared to your feed with a card!'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSharing(false); }
  };

  // Native share (mobile) → falls back to download on desktop.
  const shareNative = async () => {
    setDownloadingCard(true);
    try {
      const res = await axios.get(cardUrl, { responseType: 'blob' });
      const blob = res.data;
      const file = new File([blob], `theclub-${gameLabel}-${round.id.slice(0, 6)}.png`, { type: 'image/png' });
      const caption = `${gameLabel} at ${round.course_name}. Run your side games with The Club → theclubgolf.co`;

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'The Club — Round Results', text: caption });
        toast.success('Opened share sheet');
      } else {
        // Desktop fallback: trigger download
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success('Share card downloaded');
      }
    } catch (e) {
      // AbortError = user cancelled the share sheet, not a failure
      if (e.name !== 'AbortError') toast.error('Couldn\'t generate share card');
    } finally { setDownloadingCard(false); }
  };

  return (
    <div data-testid="round-summary">
      <div className="flex items-center gap-3 mb-4">
        <Button variant="ghost" size="icon" onClick={onBack} className="rounded-full"><ArrowLeft className="w-5 h-5" /></Button>
        <div className="flex-1">
          <h2 className="font-serif text-lg font-bold text-foreground">Round Complete</h2>
          <p className="text-xs text-muted-foreground">{gameLabel} — {round.course_name}</p>
        </div>
        <span className="px-3 py-1 rounded-full text-[10px] font-bold uppercase bg-green-500/10 text-green-600">Finished</span>
      </div>

      {/* Share card preview */}
      <div className="rounded-xl border border-border bg-card overflow-hidden mb-4" data-testid="share-card-preview">
        <img src={cardUrl} alt="Round share card" className="w-full block" />
      </div>

      <LiveScorecard round={round} />
      <Settlement results={results} />

      <div className="space-y-2 mt-4">
        <Button onClick={shareNative} disabled={downloadingCard} className="w-full h-11 rounded-lg gap-2 bg-primary text-primary-foreground" data-testid="share-card-btn">
          {downloadingCard ? 'Generating...' : 'Share to Social (Instagram, X, …)'}
        </Button>
        <Button onClick={shareToFeed} disabled={sharing} variant="outline" className="w-full h-11 rounded-lg gap-2" data-testid="share-to-feed-btn">
          {sharing ? 'Posting...' : 'Post to The Club Feed'}
        </Button>
        <Button onClick={saveToScores} disabled={savingScore} variant="outline" className="w-full h-11 rounded-lg gap-2" data-testid="save-to-scores-btn">
          {savingScore ? 'Saving...' : 'Save to My Scores'}
        </Button>
      </div>
    </div>
  );
};

// =================== LIVE SCORING ROUTER ===================
export const LiveScoring = ({ roundId, onBack }) => {
  const [round, setRound] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchRound = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/betting/rounds/${roundId}`);
      setRound(res.data);
    } catch { toast.error('Failed to load round'); }
    finally { setLoading(false); }
  }, [roundId]);

  useEffect(() => { fetchRound(); }, [fetchRound]);

  const completeRound = async () => {
    try {
      const res = await axios.put(`${API}/betting/rounds/${roundId}/complete`);
      setRound(res.data);
      toast.success('Round complete!');
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="animate-pulse"><div className="h-48 rounded-xl bg-muted" /></div>;
  if (!round) return null;

  if (round.status === 'completed') {
    return <RoundSummary round={round} onBack={onBack} />;
  }

  const gameType = round.games?.[0]?.type;
  const ScoringView = {
    nassau: NassauScoring,
    skins: SkinsScoring,
    wolf: WolfScoring,
    bingo_bango_bongo: BBBScoring,
    match_play: MatchPlayScoring,
    stableford: StablefordScoring,
    hammer: HammerScoring,
    dots: DotsScoring,
    trash: TrashScoring,
    best_ball: BestBallScoring,
    scramble: ScrambleScoring,
  }[gameType] || NassauScoring;

  return (
    <div data-testid="live-scoring-view">
      <ScoringHeader round={round} onBack={onBack} onComplete={completeRound} />
      <ScoringView round={round} onUpdate={fetchRound} />
    </div>
  );
};
